// CTSK-514 — the notes wall: Masonry (JS-placed, shortest column first), Grid
// (equal-height rows, saved height ignored) and List (one column). Cards are
// reordered with dnd-kit (grip handle, pointer / touch / keyboard); the caller
// persists the move. Layout maths lives in layout.ts.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { Note, NoteGridType } from '@captain-tasko/shared';
import { NoteCard, type NoteCardProps } from './NoteCard.js';
import { WALL_GAP, columnCount, columnWidth, dropTarget, masonryLayout } from './layout.js';
import './notes.css';

export type DropAnchor = { beforeId: string } | { afterId: string };

export interface NotesWallProps
  extends Pick<NoteCardProps, 'currentUserId' | 'canEdit' | 'canDelete' | 'onUpdate' | 'onDelete' | 'onNavigate' | 'onResize'> {
  notes: Note[];
  gridType: NoteGridType;
  /** Provide to enable drag-reordering. */
  onReorder?: (noteId: string, anchor: DropAnchor, displayedIds: string[]) => void;
  highlightId?: string | null;
}

const ESTIMATED_HEIGHT = 160;

function Item({
  note, sortable, style, measure, children,
}: {
  note: Note;
  sortable: boolean;
  style?: CSSProperties;
  measure?: (el: HTMLElement | null, id: string) => void;
  children: (p: { handle: Record<string, unknown> | null; isDragging: boolean }) => ReactNode;
}) {
  const s = useSortable({ id: note.id, disabled: !sortable });
  const t = s.transform;
  const dragShift = t ? ` translate3d(${Math.round(t.x)}px, ${Math.round(t.y)}px, 0)` : '';
  const base = style?.transform ?? '';
  return (
    <div
      ref={(el) => {
        s.setNodeRef(el);
        measure?.(el, note.id);
      }}
      className="note-wrap"
      data-note-id={note.id}
      style={{ ...style, transform: `${base}${dragShift}`.trim() || undefined, transition: s.isDragging ? 'none' : style?.transition, zIndex: s.isDragging ? 5 : undefined }}
    >
      {children({ handle: sortable ? { ...s.attributes, ...s.listeners } : null, isDragging: s.isDragging })}
    </div>
  );
}

export function NotesWall({ notes, gridType, onReorder, highlightId, ...card }: NotesWallProps) {
  const wallRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const observer = useRef<ResizeObserver | null>(null);
  const els = useRef(new Map<string, HTMLElement>());

  useLayoutEffect(() => {
    const el = wallRef.current;
    if (!el) return;
    setWidth(Math.floor(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => setWidth(Math.floor(entries[0]!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      setHeights((prev) => {
        let next = prev;
        for (const e of entries) {
          const id = (e.target as HTMLElement).dataset.noteId;
          if (!id) continue;
          const h = Math.ceil(e.borderBoxSize?.[0]?.blockSize ?? e.contentRect.height);
          if (h > 0 && prev[id] !== h) {
            if (next === prev) next = { ...prev };
            next[id] = h;
          }
        }
        return next;
      });
    });
    observer.current = ro;
    els.current.forEach((el) => ro.observe(el));
    return () => { ro.disconnect(); observer.current = null; };
  }, []);

  const measure = useCallback((el: HTMLElement | null, id: string) => {
    const prev = els.current.get(id);
    if (prev && prev !== el) observer.current?.unobserve(prev);
    if (el) {
      els.current.set(id, el);
      observer.current?.observe(el);
    } else {
      els.current.delete(id);
    }
  }, []);

  const w = width || 1100; // jsdom / first paint: assume the full page width
  const cols = columnCount(w, gridType);
  const colW = columnWidth(w, cols);
  const ids = useMemo(() => notes.map((n) => n.id), [notes]);
  const layout = useMemo(
    () => (gridType === 'masonry' ? masonryLayout(ids.map((id) => heights[id] ?? ESTIMATED_HEIGHT), cols, colW) : null),
    [gridType, ids, heights, cols, colW],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(e: DragEndEvent) {
    if (!onReorder || !e.over) return;
    const target = dropTarget(ids, String(e.active.id), String(e.over.id));
    if (target) onReorder(String(e.active.id), target, ids);
  }

  const strategy = gridType === 'list' ? verticalListSortingStrategy : gridType === 'grid' ? rectSortingStrategy : () => null;
  const sortable = Boolean(onReorder);

  // Scroll to a permalink target once it is on the wall and laid out.
  const scrolled = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightId || scrolled.current === highlightId || !ids.includes(highlightId)) return;
    const t = window.setTimeout(() => {
      document.getElementById(`note-${highlightId}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      scrolled.current = highlightId;
    }, 150);
    return () => window.clearTimeout(t);
  }, [highlightId, ids, layout]);

  const wallStyle: CSSProperties =
    gridType === 'masonry'
      ? { height: layout?.height ?? 0 }
      : gridType === 'grid'
        ? ({ '--note-cols': cols } as CSSProperties)
        : {};

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={strategy}>
        <div ref={wallRef} className={`notes-wall notes-wall--${gridType}`} style={wallStyle} data-testid="notes-wall" data-grid={gridType} data-cols={cols}>
          {notes.map((n, i) => {
            const p = layout?.placements[i];
            const style: CSSProperties | undefined = p
              ? { width: colW, transform: `translate(${p.x}px, ${p.y}px)` }
              : undefined;
            return (
              <Item key={n.id} note={n} sortable={sortable} style={style} measure={gridType === 'masonry' ? measure : undefined}>
                {({ handle, isDragging }) => (
                  <NoteCard
                    note={n}
                    {...card}
                    ignoreSavedHeight={gridType === 'grid'}
                    dragHandle={handle as never}
                    isDragging={isDragging}
                    highlighted={highlightId === n.id}
                  />
                )}
              </Item>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export { WALL_GAP };
